import LinkOrSpan from "@/components/links/LinkOrSpan/LinkOrSpan";
import Skeleton from "@/components/Skeleton/Skeleton";
import { VisualizationProps } from "@/components/visualizations/Visualization";
import { formatPercentage } from "@/pages/dashboard/utils/formatters";
import { DownCircleOutlined } from "@ant-design/icons";
import { Fragment, ReactNode } from "react";
import NoDataPoints from "../NoDataPoints/NoDataPoints";
import useVisualization from "../useVisualization";
import styles from "./List.module.scss";

export interface ListDataItem {
  label: ReactNode | string;
  value: number;
  icon?: ReactNode;
  extraProp?: { label?: string; value: string };
  type?: ReactNode;
  subType?: ReactNode;
  to?: string;
}

export interface ListData<R = ListDataItem> {
  list: R[];
  total?: number;
}

export interface ListProps<R = ListDataItem> extends VisualizationProps {
  data: ListData<R>;
  loadMore?: {
    show: boolean;
    fn: () => void;
    numberOfMore?: number;
  };
  customRenderer?: (item: R) => ReactNode;
}

export default function List<R extends ListDataItem>(props: ListProps<R>) {
  const { data, loading, loadMore } = props;
  const { formatLabel } = useVisualization(props);

  const percentageLabel = (value: number) => {
    if (!data.total) return "-";
    return formatPercentage(value / data.total);
  };

  return (
    <div className={styles.List}>
      {loading ? (
        [...Array(5)].map((_, index) => (
          <Fragment key={"skeleton" + index}>
            <div className={styles.divider}></div>
            <Skeleton style={{ height: "48px", width: "100%" }} />
          </Fragment>
        ))
      ) : data.list.length === 0 ? (
        <NoDataPoints />
      ) : (
        data.list.map((item, index) => (
          <Fragment key={"listitem" + index}>
            <div className={styles.divider}></div>
            <LinkOrSpan
              to={item.to}
              className={styles["list-item"]}
            >
              <div className={styles["number-or-icon"]}>{item.icon || `${index + 1}.`}</div>
              <div className={styles.item}>
                <div className={styles.header}>
                  <div className={styles.title}>
                    {props.customRenderer ? props.customRenderer(item) : item.label}
                  </div>
                  {item.extraProp && (
                    <div className={styles["extra-properties"]}>
                      {item.extraProp.label
                        ? `${item.extraProp.label}: ${item.extraProp.value}`
                        : item.extraProp.value}
                    </div>
                  )}
                </div>
                <div className={styles.data}>
                  {data.total && (
                    <>
                      <span>{percentageLabel(item.value)}</span>
                      <span className={styles["data-divider"]}></span>
                    </>
                  )}
                  <span>{formatLabel(item.value)}</span>
                  {item.type && (
                    <>
                      <span className={styles["data-divider"]}></span>
                      <span className={styles["type-text"]}>{item.type}</span>
                    </>
                  )}
                  {item.subType && (
                    <>
                      <span className={styles["data-divider"]}></span>
                      <span className={styles["type-text"]}>{item.subType}</span>
                    </>
                  )}
                </div>
              </div>
            </LinkOrSpan>
          </Fragment>
        ))
      )}
      {loadMore &&
        data.list.length > 0 &&
        loadMore.show &&
        (loading ? (
          <Skeleton style={{ margin: "0 auto", height: "14px", width: "75px" }} />
        ) : (
          <span
            className={styles["load-more"]}
            onClick={() => loadMore.fn()}
          >
            <DownCircleOutlined /> Show{loadMore.numberOfMore && ` ${loadMore.numberOfMore}`} more
          </span>
        ))}
    </div>
  );
}
